import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { styled } from '@mui/styles';
import { Text, SearchBar, Theme, InfoCard, MessageCard, Content, Container, Title, i18n, tokens } from '@simplybenefits/sb-ui-library';
import { sendQuery } from '../../actions/coverage.js';
import { setCurrentSidebarItem } from '../../actions/user.js';
import { globalStyles } from '../../theme/index.js';
import { numberWithCommas } from '../../services/helpers.js';

import CriticalIllness from './CriticalIllness.js';
import DeathDismemberment from './DeathDismemberment.js';
import Dental from './Dental.js';
import DependentLife from './DependentLife.js';
import Drugs from './Drugs.js';
import Efap from './Efap.js';
import LifeInsurance from './LifeInsurance.js';
import Ltd from './Ltd.js';
import MajorMedical from './MajorMedical.js';
import OutOfCountry from './OutOfCountry.js';
import Paramedicals from './Paramedicals.js';
import SpendingAccounts from './SpendingAccounts.js';
import Std from './Std.js';
import Vision from './Vision.js';
import VirtualHealthcare from './VirtualHealthcare.js';
import FinancialAwareness from './FinancialAwareness.js';

import store from '../../redux/store.js';


const StyledColumn = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
    [theme.breakpoints.down(1280)]: {
        width: '100%',
        flexDirection: 'column',
    }
}));

const StyledLeft = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up(1280)]: {
        minWidth: '50%'
    },
    [theme.breakpoints.down(1280)]: {
        minWidth: '100%',
        maxWidth: '100%',
    }
}));

const StyledRight = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up(1280)]: {
        minWidth: '50%'
    },
    [theme.breakpoints.down(1280)]: {
        minWidth: '100%',
        maxWidth: '100%',
    }
}));

class Coverage extends PureComponent {
    state = {
        expanded: [],
        query: '',
        answer: null
    };

    componentDidMount() {
        this.props.dispatch(setCurrentSidebarItem('coverage'));
    }

    _search = async (query) => {
        const answer = await this.props.dispatch(sendQuery(query));
        this.setState({
            answer
        });
    };

    _closeAnswer = () => {
        this.setState({
            answer: null
        });
    };

    _handleInputChange = (type, event) => {
        this.setState({
            [type]: event.target.value
        });
    };

    _handleChange = (panel) => (event, expanding) => {
        let expanded = [ ...this.state.expanded ];
        const index = expanded.indexOf(panel);

        if (expanding && index === -1) {
            expanded.push(panel);
        } else {
            expanded.splice(index, 1);
        }

        this.setState({
            expanded
        });
    };

    _defineCoverageType = (coverage, perClaim = false) => {
        const deductibleType =  perClaim ? i18n.t(tokens.labels.coverage.claim) : i18n.t(tokens.labels.year);

        switch (this.props.meta.coverageType) {
            case 'Solo':
                return coverage.soloDeductibleAmount ?
                <Text small margin="5px 0px 10px 0px">{ i18n.t(tokens.labels.coverage.soloDeductibleAmount, { soloDeductibleAmount: numberWithCommas(coverage.soloDeductibleAmount), deductibleType }) }</Text>
                : null;
            case 'Duo':
                return coverage.duoDeductibleAmount ?
                <Text small margin="5px 0px 10px 0px">{ i18n.t(tokens.labels.coverage.duoDeductibleAmount, { duoDeductibleAmount: numberWithCommas(coverage.duoDeductibleAmount), deductibleType }) }</Text>
                : null;
            case 'Family':
                return coverage.familyDeductibleAmount ?
                <Text small margin="5px 0px 10px 0px">{ i18n.t(tokens.labels.coverage.familyDeductibleAmount, { familyDeductibleAmount: numberWithCommas(coverage.familyDeductibleAmount), deductibleType }) }</Text>
                : null;
            default:
                return null;
        }
    };

    _defineSpendingAccountType = (coverage) => {
        let coverageType = 'N/A';
        
        if (coverage.meta.isPooled === 0 && coverage.health.hasBenefit === 1) {
            coverageType = i18n.t(tokens.labels.health);
        } else if (coverage.meta.isPooled === 0 && coverage.lifestyle.hasBenefit === 1) {
            coverageType = i18n.t(tokens.labels.lifestyle);
        } else if (coverage.meta.isPooled === 1 || (coverage.health.hasBenefit === 1 && coverage.lifestyle.hasBenefit === 1)) {
            coverageType = i18n.t(tokens.labels.coverage.healthAndLifestyleIncluded);
        }

        return coverageType;
    };

    render() {
        const { classes, meta } = this.props;
        const { expanded } = this.state;
        const isDependent = meta.isDependent;
        let waivedEHC = false;
        let waivedDental = false;
        let showWaiveForHealthDependentWarning = false;
        let showWaiveForDentalDependentWarning = false;

        if (meta.hasWaived && meta.hasWaived.includes('Health')) {
            waivedEHC = !meta.waiveFor.includes('Self') ?
                isDependent : true;

            showWaiveForHealthDependentWarning = true;
        }

        if (meta.hasWaived && meta.hasWaived.includes('Dental')) {
            waivedDental = !meta.waiveFor.includes('Self') ?
                isDependent : true;

            showWaiveForDentalDependentWarning = true;
        }

        const coverages = [];
        const left = [];
        const right = [];

        const {
            lifeInsurance,
            deathDismemberment,
            dental,
            drugs,
            majorMedical,
            paramedicals,
            vision,
            efap,
            dependentLife,
            ltd,
            std,
            spendingAccounts,
            criticalIllness,
            outOfCountry,
            virtualHealthcare,
            financialAwareness
        } = this.props.coverage;

        const styles = {
            contentBlock: globalStyles.contentBlock,
            spendingRow: {
                display: 'flex',
                flexDirection: 'column',
                minWidth: '50%',
                maxWidth: '50%',
            },
            spendingColumns: {
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'baseline',
                marginBottom: 35,
                
            },
            left: {
                display: 'flex',
                flexDirection: 'column',
                minWidth: '50%',
                maxWidth: '50%',
            },
            right: {
                display: 'flex',
                flexDirection: 'column',
                minWidth: '50%',
                maxWidth: '50%',
            },
            block: {
                flex: 1,
                padding: "10px"
            },
            panelDetails: {
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                padding: '0 20px 0 0'
            },
            expanded: {
                "&$expanded": {
                    '&>:last-child': {
                        color: `${ Theme.colors.mainTextGrey } !important`,
                        fontSize: 16,
                        fontWeight: '500 !important',
                        marginRight: '-12px !important'
                    },
                    backgroundColor: Theme.colors.surface,
                    minHeight: '24px !important',
                    margin: '0 !important'
                }
            },
            panelContent: {
                color: Theme.colors.mainTextGrey,
                paddingLeft: 0,
                paddingRight: 0,
                margin: 0
            },
            root: {
                paddingLeft: 0,
                paddingRight: 0,
                minHeight: '50px !important',
                borderBottomWidth: 1,
                borderBottomColor: Theme.colors.lightGrey,
                borderBottomStyle: 'solid'
            },
            panelSpare: {
                height: 48,
                cursor: 'default',
                backgroundColor: Theme.colors.surface
            },
            row: {
                display: 'flex',
                flex: 1,
                flexDirection: 'row',
                alignItems: 'center'
            },
            title: {
                flex: 1
            },
            accordionSummary: {
                "& .MuiAccordionSummary-expandIconWrapper": {
                    color: Theme.colors.mainTextGrey,
                    width: 24,
                    height: 24,
                    padding: 3,  
                    justifyContent: "center",
                    alignItems: "center",
                },
                '& .MuiAccordionSummary-expandIconWrapper:hover': {
                    backgroundColor: "#bfbfbf",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: "50%",
                },
                '&.MuiAccordionSummary-root': {
                    borderColor: "#e6e7e8",
                    borderBottomStyle: "solid",
                    borderBottomWidth: "1px"
                },
            }
        }


        if (!isDependent && lifeInsurance.benefitActive === 1) {
            coverages.push(<LifeInsurance
                key="lifeInsurance"
                classes={ styles }
                expanded={ expanded }
                lifeInsurance={ lifeInsurance }
                handleChange={ this._handleChange }
            />);
        }

        if (!isDependent && dependentLife.benefitActive === 1) {
            coverages.push(<DependentLife
                key="dependentLife"
                classes={ styles }
                expanded={ expanded }
                dependentLife={ dependentLife }
                handleChange={ this._handleChange }
            />);
        }

        if (!isDependent && std.benefitActive === 1) {
            coverages.push(<Std
                key="std"
                classes={ styles }
                expanded={ expanded }
                std={ std }
                handleChange={ this._handleChange }
            />);
        }

        if (dental.benefitActive === 1) {
            coverages.push(<Dental
                key="dental"
                classes={ styles }
                expanded={ expanded }
                dental={ dental }
                handleChange={ this._handleChange }
                defineCoverageType={ this._defineCoverageType }
                showDependentWarning={ showWaiveForDentalDependentWarning }
                waivedDental={ waivedDental }
            />);
        }

        if (paramedicals.benefitActive === 1 &&
            (
                paramedicals.reimbursement !== 0 ||
                (paramedicals.hasMentalWellnessGrouped !== 0 && paramedicals.mentalWellnessReimbursement !== 0) ||
                paramedicals.mentalWellnessMaximum !== 0
            )
        ) {
            coverages.push(<Paramedicals
                key="paramedicals"
                classes={ styles }
                expanded={ expanded }
                paramedicals={ paramedicals }
                handleChange={ this._handleChange }
                defineCoverageType={ () => this._defineCoverageType(majorMedical) }
                showDependentWarning={ showWaiveForHealthDependentWarning }
                waivedEHC={ waivedEHC }
                hasDeductible={ majorMedical.hasDeductible }
            />);
        }

        if (vision.benefitActive === 1) {
            coverages.push(<Vision
                key="vision"
                classes={ styles }
                expanded={ expanded }
                vision={ vision }
                handleChange={ this._handleChange }
                showDependentWarning={ showWaiveForHealthDependentWarning }
                waivedEHC={ waivedEHC }
            />);
        }

        if (!isDependent && efap.benefitActive === 1) {
            coverages.push(<Efap
                key="efap"
                classes={ styles }
                expanded={ expanded }
                efap={ efap }
                handleChange={ this._handleChange }
                theme={ Theme }
                waivedEHC={ waivedEHC }
            />);
        }

        if (!isDependent && virtualHealthcare.benefitActive === 1) {
            coverages.push(<VirtualHealthcare
                key="virtualHealthcare"
                classes={ styles }
                expanded={ expanded }
                virtualHealthcare={ virtualHealthcare }
                handleChange={ this._handleChange }
                theme={ Theme }
            />);
        }

        if (financialAwareness.benefitActive === 1) {
            coverages.push(<FinancialAwareness
                key="financialAwareness"
                classes={ styles }
                expanded={ expanded }
                financialAwareness={ financialAwareness }
                handleChange={ this._handleChange }
                theme={ Theme }
            />);
        }

        if (!isDependent && deathDismemberment.benefitActive === 1) {
            coverages.push(<DeathDismemberment
                key="deathDismemberment"
                classes={ styles }
                expanded={ expanded }
                deathDismemberment={ deathDismemberment }
                handleChange={ this._handleChange }
            />);
        }

        if (!isDependent && ltd.benefitActive === 1) {
            coverages.push(<Ltd
                key="ltd"
                classes={ styles }
                expanded={ expanded }
                ltd={ ltd }
                handleChange={ this._handleChange }
            />);
        }

        if (!isDependent && criticalIllness.benefitActive === 1) {
            coverages.push(<CriticalIllness
                key="criticalIllness"
                classes={ styles }
                expanded={ expanded }
                criticalIllness={ criticalIllness }
                handleChange={ this._handleChange }
            />);
        }

        if (drugs.benefitActive === 1) {
            coverages.push(<Drugs
                key="drugs"
                classes={ styles }
                expanded={ expanded }
                drugs={ drugs }
                handleChange={ this._handleChange }
                defineCoverageType={ this._defineCoverageType }
                showDependentWarning={ showWaiveForHealthDependentWarning }
                waivedEHC={ waivedEHC }
            />);
        }

        if (majorMedical.benefitActive === 1) {
            coverages.push(<MajorMedical
                key="majorMedical"
                classes={ styles }
                expanded={ expanded }
                majorMedical={ majorMedical }
                handleChange={ this._handleChange }
                defineCoverageType={ this._defineCoverageType }
                showDependentWarning={ showWaiveForHealthDependentWarning }
                waivedEHC={ waivedEHC }
                
            />);
        }

        if (outOfCountry.benefitActive === 1) {
            coverages.push(<OutOfCountry
                key="outOfCountry"
                classes={ styles }
                expanded={ expanded }
                outOfCountry={ outOfCountry }
                handleChange={ this._handleChange }
                theme={ Theme }
                showDependentWarning={ showWaiveForHealthDependentWarning }
                waivedEHC={ waivedEHC }
            />);
        }

        if (!isDependent
            && (spendingAccounts.health.hasBenefit === 1 || spendingAccounts.lifestyle.hasBenefit === 1)
        ) {
            coverages.push(<SpendingAccounts
                key="defineSpendingAccountType"
                classes={ styles }
                expanded={ expanded }
                spendingAccounts={ spendingAccounts }
                handleChange={ this._handleChange }
                defineSpendingAccountType={ this._defineSpendingAccountType }
                healthRemaining={ this.props.healthRemaining }
                lifestyleRemaining={ this.props.lifestyleRemaining }
                lsaClaimTypes={ this.props.lsaClaimTypes }
            />);
        }

        coverages.map((section, index) => {
            index % 2 === 0 ? left.push(section) : right.push(section);
            return null;
        });
        
        return (
            <Container>
                <Content>
                    <Title title={ i18n.t(tokens.titles["my plan coverage"])} />

                    <Text medium bold margin={'20px 0 0 0'}>{ i18n.t(tokens.labels.coverage.wantToKnowWhatsCovered) }</Text>

                    <SearchBar
                        onChange={ (value) => this._handleInputChange('query', value) }
                        search={ this._search }
                        placeholder={ i18n.t(tokens.labels.coverage.askAwayWellAnswer) }
                        iconColor={ store.getState().whitelabel.primaryColor || Theme.colors.skyBlue }
                        customStyle={{
                            margin: '20px auto',
                            width: '100%',
                            backgroundColor: Theme.colors.surface
                        }}
                    />

                    {
                        this.state.answer &&
                            <React.Fragment>
                                <InfoCard
                                    title={ i18n.t(tokens.labels.coverage.answer) }
                                    paragraph={ this.state.answer }
                                    onClose={ this._closeAnswer }
                                />
                            </React.Fragment>
                    }

                    {
                        left.length > 0 ?
                            <div style={ styles.contentBlock }>
                                <StyledColumn>
                                    <StyledLeft>
                                        {
                                            left.map((section, index) => section)
                                        }
                                    </StyledLeft>

                                    <StyledRight>
                                        {
                                            right.map((section, index) => section)
                                        }
                                    </StyledRight>
                                </StyledColumn>
                            </div>
                        :
                            <MessageCard
                                title={ i18n.t(tokens.titles.noCoverage) }
                                subtitle={ i18n.t(tokens.labels.coverage.coverageWaivedMessage)}
                                small
                            />
                    }
                </Content>

                <span id="coverage" />
            </Container>
        );
    }
}

Coverage.propTypes = {
    loading: PropTypes.bool,
    coverage: PropTypes.object,
    meta: PropTypes.object,
    lsaClaimTypes: PropTypes.array,
    healthRemaining: PropTypes.number,
    lifestyleRemaining: PropTypes.number,
    whitelabel: PropTypes.object
};

const mapStateToProps = state => ({
    loading: state.coverage.loading,
    coverage: state.coverage.data,
    meta: state.user.meta,
    healthRemaining: state.spendingAccounts.health.remaining,
    lsaClaimTypes: state.spendingAccounts.lsaClaimTypes,
    lifestyleRemaining: state.spendingAccounts.lifestyle.remaining,
    whitelabel: state.whitelabel
});

export default (connect(mapStateToProps)(Coverage));
